import { Loader2 } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { StrongText } from "@/components/strong-text";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useBlockMcpUser, useUnblockMcpUser } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";

/** Someone to block or unblock, as the people table knows them. */
export interface BlockTarget {
  /** Who it is, as the table shows them: their name, else their email. */
  label: string;
  /** Their Microsoft (Entra) id, by which the connector blocks them; null if never seen. */
  userOid: string | null;
  /** Blocked now: the dialog then unblocks. */
  blocked: boolean;
}

interface BlockDialogProps {
  /** The person to block, or to unblock when they are blocked already. */
  person: BlockTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirms blocking or unblocking a person's use of the connector, from the
 * Teams page's people. The block is Velta's own list, by the person's
 * Microsoft id, enforced by the MCP server on top of its rule that only De
 * Ceuster's own Microsoft accounts get in. The account itself stays as it is:
 * De Ceuster's IT provider manages it in their Microsoft tenant. The hooks
 * write the confirmed block into the Teams page at once and refetch it.
 *
 * While the request runs the dialog cannot be dismissed (Cancel, Escape, the
 * close button or a click outside): closing would not stop the request, so the
 * admin would believe they had cancelled a block that still lands. On close
 * the focus goes back to the button that opened it: a dialog without a
 * Radix trigger would drop it on the page's body.
 */
export function BlockDialog({ person, open, onOpenChange }: BlockDialogProps) {
  const { t } = useTranslation();
  const blockMutation = useBlockMcpUser();
  const unblockMutation = useUnblockMcpUser();
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const unblocking = !!person?.blocked;
  const pending = blockMutation.isPending || unblockMutation.isPending;
  // What had the focus as the dialog opened, read before its content takes it.
  const openerRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (open) {
      openerRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setFailure(null);
    }
    onOpenChange(next);
  };

  const handleDismiss = (next: boolean) => {
    if (!next && pending) {
      return;
    }
    handleOpenChange(next);
  };

  const handleConfirm = async () => {
    if (!person?.userOid) {
      return;
    }
    setFailure(null);
    try {
      if (unblocking) {
        await unblockMutation.mutateAsync({ userOid: person.userOid });
      } else {
        await blockMutation.mutateAsync({ userOid: person.userOid });
      }
      handleOpenChange(false);
    } catch (err) {
      setFailure(inlineFailure({ error: err }));
    }
  };
  const error = failure && apiErrorMessage(failure.error, unblocking ? "unblockUser" : "blockUser");

  return (
    <Dialog open={open} onOpenChange={handleDismiss}>
      <DialogContent
        className="sm:max-w-md"
        onCloseAutoFocus={(event) => {
          // A row's button, still there; a phone menu's item is gone by now.
          const opener = openerRef.current;
          if (opener?.isConnected) {
            event.preventDefault();
            opener.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {unblocking ? t("teams.block.unblockTitle") : t("teams.block.title")}
          </DialogTitle>
          <DialogDescription>
            <StrongText
              i18nKey={unblocking ? "teams.block.unblockBody" : "teams.block.body"}
              values={{ who: person?.label ?? "" }}
            />
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-destructive text-sm">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleDismiss(false)} disabled={pending}>
            {t("common.cancel")}
          </Button>
          <Button
            variant={unblocking ? "default" : "destructive"}
            onClick={handleConfirm}
            disabled={pending || !person?.userOid}
          >
            {pending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : unblocking ? (
              t("teams.block.unblock")
            ) : (
              t("teams.block.block")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
